import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Badge, Input, RegionState } from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';

import { ListSkeleton } from '../../components/Skeletons';
import { EligiblePartnersDocument } from '../../graphql/__generated__/types';
import { partnerListRefusalOf } from './refusals';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface PickedPartner {
    sourcePartnerId: string;
    name: string;
    externalReference: string | null;
}

interface Props {
    excludePartnerListId?: string;
    picked: PickedPartner[];
    onPicked: (picked: PickedPartner[]) => void;
    searchPlaceholder: string;
    emptyTitle: string;
    emptyDescription: string;
}

// The search runs on the server (paginated referential), and the picked rows are pinned
// to the top so a ticked partner never leaves the DOM when the search stops matching it.
const EligiblePartnerPicker = ({
    excludePartnerListId,
    picked,
    onPicked,
    searchPlaceholder,
    emptyTitle,
    emptyDescription,
}: Props) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const { data, loading, error } = useQuery(EligiblePartnersDocument, {
        variables: {
            search: debouncedSearch || null,
            excludePartnerListId: excludePartnerListId ?? null,
            limit: PAGE_SIZE,
            offset: 0,
        },
        fetchPolicy: 'cache-and-network',
    });

    const totalCount = data?.eligiblePartners?.totalCount ?? null;
    const items = data?.eligiblePartners?.items ?? [];
    const pickedIds = useMemo(() => new Set(picked.map((partner) => partner.sourcePartnerId)), [picked]);
    const unpicked = items.filter((partner) => !pickedIds.has(partner.sourcePartnerId));

    const toggle = (partner: PickedPartner) => {
        if (pickedIds.has(partner.sourcePartnerId)) {
            onPicked(picked.filter((entry) => entry.sourcePartnerId !== partner.sourcePartnerId));
            return;
        }
        onPicked([...picked, partner]);
    };

    const row = (partner: PickedPartner, isPicked: boolean) => (
        <label
            key={partner.sourcePartnerId}
            className="flex cursor-pointer items-center gap-3 border-b border-border-weak px-3 py-2 last:border-b-0 hover:bg-background-weak"
        >
            <input
                type="checkbox"
                checked={isPicked}
                onChange={() => toggle(partner)}
                className="size-4 shrink-0 accent-primary"
            />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{partner.name}</span>
            <span className="shrink-0 font-mono text-xs text-text-600">{partner.externalReference}</span>
        </label>
    );

    const refusal = error ? partnerListRefusalOf(error) : null;
    const showSkeleton = loading && items.length === 0 && picked.length === 0;

    return (
        <div className="flex flex-col gap-3">
            <Input
                value={search}
                onValue={setSearch}
                clearable
                prefix={<MagnifyingGlass size={16} />}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
            />

            <div className="max-h-72 overflow-y-auto rounded-md border border-border-norm">
                {refusal ? (
                    <RegionState tone="error" title={refusal.title} description={refusal.guidance} />
                ) : showSkeleton ? (
                    <p className="px-3 py-6 text-center text-sm text-text-600">
                        <ListSkeleton rows={5} withControl />
                    </p>
                ) : picked.length === 0 && unpicked.length === 0 ? (
                    <RegionState
                        title={debouncedSearch ? c('Partner picker').t`No eligible partner matches` : emptyTitle}
                        description={
                            debouncedSearch
                                ? c('Partner picker')
                                      .t`The search runs on the company name and on the Salesforce external identifier, never on the theme of a list.`
                                : emptyDescription
                        }
                    />
                ) : (
                    <>
                        {picked.map((partner) => row(partner, true))}
                        {unpicked.map((partner) => row(partner, false))}
                    </>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-text-600">
                <Badge size="small" category={picked.length > 0 ? 'info' : 'default'}>
                    {c('Partner picker').ngettext(
                        msgid`${picked.length} partner selected`,
                        `${picked.length} partners selected`,
                        picked.length
                    )}
                </Badge>
                {totalCount !== null && (
                    <span>
                        {totalCount > PAGE_SIZE
                            ? c('Partner picker')
                                  .t`${totalCount} eligible partners answer this search, and the first ${PAGE_SIZE} are shown. Refine the search to reach the others.`
                            : c('Partner picker').ngettext(
                                  msgid`${totalCount} eligible partner answers this search.`,
                                  `${totalCount} eligible partners answer this search.`,
                                  totalCount
                              )}
                    </span>
                )}
            </div>
        </div>
    );
};

export default EligiblePartnerPicker;
